<template>
  <span v-tooltip="finalName" v-bind="$attrs" class="c-avatar" :class="classes" :style="styles">
    <img v-if="finalImgSrc" :src="finalImgSrc" :alt="finalName">
    <span v-else-if="finalInitials">{{ finalInitials }}</span>
    <CIcon v-else :name="icon" :size="12*size"/>
  </span>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import ChatUser from '@/chat/models/ChatUser';
  import Customer from '@/customer/models/Customer';
  import ReferenceService from '@/general/services/ReferenceService';
  import User, { UserId } from '../models/User';
  import CIcon from './CIcon.vue';

  const props = withDefaults(defineProps<{
    person?: Customer | User | ChatUser | null,
    userId?: UserId,
    imgSrc?: string,
    fullName?: string,
    size?: 1.5 | 2 | 2.5 | 3 | 3.5 | 4 | 5,
    initials?: string,
    icon?: string,
  }>(), {
    size: 2,
    icon: 'avatar',
  });

  const finalPerson = computed(() => props.person ?? ReferenceService.getUser(props.userId));

  const finalName = computed(() => props.fullName || finalPerson.value?.fullName);

  const finalImgSrc = computed(() => props.imgSrc || finalPerson.value?.avatar);

  const finalInitials = computed(() => {
    return props.initials || finalName.value?.split(' ').slice(0, 2).map(n => n.charAt(0)).join('');
  });

  const classes = computed(() => {
    if (finalImgSrc.value) return;
    if (finalInitials.value) return 'c-avatar-initials';
    return 'c-avatar-anonymous';
  });

  const styles = computed(() => {
    return {
      height: `${props.size}rem`,
      fontSize: `${props.size / 2}rem`, // for initials
    };
  });
</script>

<style lang="scss">
  .c-avatar {
    border-radius: 50%;
    display: inline-grid;
    place-items: center;
    overflow: hidden;
    flex-shrink: 0;
    aspect-ratio: 1;

    &-initials {
      background-color: $info;
      color: $neutral-0;
    }

    &-anonymous {
      background-color: $neutral;
      color: $neutral-0;
    }
  }
</style>